// Structured clinic address helpers for the vet portal (My Profile).
//
// Ported from ada-core sites/src/lib/clinicProfile.ts (the Shop Verification
// gap-fill, 2026-09-10) so both surfaces speak the same vocabulary: the
// Payload `users.businessAddress` group — address1/city/state/postalCode/
// country — where `country` is a TWO-member enum (US | CA) and `state` is a
// two-letter code. Until 2026-09-11 My Profile wrote only the flat `address`
// + `zip` columns, so every account edited here reached the Shop Verification
// page and the CRM org with NO structured city/state (762 of 826 vets on prod
// that day). The verification page now gap-fills; this makes the profile
// write the structured shape in the first place.

export const CLINIC_COUNTRIES = ['US', 'CA'] as const
export type ClinicCountry = (typeof CLINIC_COUNTRIES)[number]

export interface StateOption {
  code: string
  name: string
}

export const US_STATES: ReadonlyArray<StateOption> = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
]

export const CA_PROVINCES: ReadonlyArray<StateOption> = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
]

/** The shape of Payload `users.businessAddress`, as the portal reads/writes it. */
export interface BusinessAddress {
  address1?: string
  address2?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  phone?: string
}

/**
 * The ONE phone rule for the clinic block: 10-15 digits, written with nothing
 * but digits, spaces, +, -, ( ). My Profile and the "Complete your clinic
 * profile" prompt both write `users.phone`; when the prompt counted digits
 * only, "call 319-842-2214" passed there, was stored verbatim, and then
 * failed My Profile's char-class check on every later save of that account.
 */
export function isValidClinicPhone(raw: string): boolean {
  const value = String(raw ?? '').trim()
  if (!/^[+\d\s\-()]+$/.test(value)) return false
  const digits = value.replace(/\D/g, '').length
  return digits >= 10 && digits <= 15
}

export function normalizeClinicCountry(raw: unknown): ClinicCountry | undefined {
  const v = String(raw ?? '')
    .trim()
    .toUpperCase()
  if (v === 'US' || v === 'USA' || v === 'UNITED STATES') return 'US'
  if (v === 'CA' || v === 'CAN' || v === 'CANADA') return 'CA'
  return undefined
}

export function stateOptionsFor(country: string | undefined): ReadonlyArray<StateOption> {
  return normalizeClinicCountry(country) === 'CA' ? CA_PROVINCES : US_STATES
}

/** A state/province typed or stored in any form → its two-letter code, or ''
 *  when it is not one of the country's states. Case-insensitive on both code
 *  and full name, so "tx", "TX" and "Texas" all resolve to "TX". */
export function resolveStateCode(raw: unknown, country: string | undefined): string {
  const v = String(raw ?? '').trim()
  if (!v) return ''
  const list = stateOptionsFor(country)
  const byCode = list.find((s) => s.code === v.toUpperCase())
  if (byCode) return byCode.code
  const byName = list.find((s) => s.name.toLowerCase() === v.toLowerCase())
  return byName ? byName.code : ''
}

/** The one-line form older readers still consume (`users.address`): street,
 *  unit, city, state — joined, blanks dropped. Postal code is NOT included:
 *  it has its own column (`users.zip`) and doubling it corrupts the regex
 *  extractors that mine ZIPs out of this blob (ops backfill 159). */
export function composeLegacyAddressLine(addr: Partial<BusinessAddress>): string {
  return [addr.address1, addr.address2, addr.city, addr.state]
    .map((p) => String(p ?? '').trim())
    .filter((p) => p !== '')
    .join(', ')
}
